import {Component, Mixins, Watch} from 'vue-property-decorator';
import {mapActions, mapGetters} from 'vuex';
import api from "@webparts/api";
import * as accountTypes from '@account/store/types';

import WebpartTemplate from "@webparts/components/webparts/base/WebpartTemplate.vue";
import WebpartBase from "@webparts/components/webparts/base/WebpartBase";
import BiChartComponent from "@webparts/components/webparts/YASKAWA/PowerBi/BiChartComponent.vue";
import Dropdown from '@webparts/components/common/Dropdown.vue';
import { LoadingBar } from '@appbase/components/loading'
import NoData from "@webparts/components/common/NoData.vue";

import {uGetDateFormat, uGetZero} from "@appbase/util";

type PowerBiType = 'book' | 'sales' | 'inventory' | 'bbr' | 'sg';
type PowerBiDataItem = { name: string, type: PowerBiType, key: string, data: object[] | null };

@Component({
    name: 'powerbi-webpart',
    components: {WebpartTemplate, BiChartComponent, Dropdown, NoData, LoadingBar},
    methods: {
        ...mapActions({
            getAccessToken: accountTypes.MS_ACCESSTOKEN,
            forceToGetAccessToken: accountTypes.MS_ACCESSTOKEN_FORCE,
        })
    },
    computed: {
        ...mapGetters({
            msAccessToken: accountTypes.MS_ACCESSTOKEN
        })
    }
})
export default class PowerBIWebpart extends Mixins(WebpartBase) {
    getAccessToken!: () => Promise<string>;
    forceToGetAccessToken!: () => Promise<string>;

    msAccessToken!: string | null;

    type: PowerBiType = 'book';
    dataSource: object[] = [];

    biDataItems: PowerBiDataItem[] = [];

    selMonth: number = this.curMonth.value;

    get curMonth() {
        return { text: this.$t(`W_${uGetDateFormat('mm')}M`), value: Number(uGetDateFormat('mm')) } ;
    }

    get prevMonth(){
        let date = new Date();
        if(date.getMonth() > 0){
            let moVal = date.getMonth();
            return { text: this.$t(`W_${moVal}M`), value: moVal };
        }
        else{
            return { text: this.$t('W_12M'), value: 12 };  // 현재가 1월이면 이전달=12월
        }
    }

    async init(){
        this.biDataItems = [
            { name: 'Book', type: 'book', key: 'book', data: null },
            { name: 'Sales', type: 'sales', key: 'sales', data: null },
            { name: 'Inv', type: 'inventory', key: 'inventory', data: null },
        ]
        this.getDataSet();
    }

    async getData(type: PowerBiType, unit: string, year: string, yearMonth: string) {
        let token = this.msAccessToken;
        if(token == null){
            token = await this.getAccessToken();
        }

        try{
            return await api.powerbi.getDataSet(token, type, unit, year, yearMonth);
        }
        catch(error){
            console.error('Error fetching Power BI data:', error);
            token = await this.getNewToken();
        }

        return await api.powerbi.getDataSet(token, type, unit, year, yearMonth);
    }

    async getNewToken(){
        try {
            let token = await this.forceToGetAccessToken();
            if(token == null || token === ''){
                throw new Error('Failed to retrieve access token');
            }
            return token;
        } catch (error) {
            console.error('Error getting new token:', error);
            throw error;
        }
    }

    async getDataSet(force: boolean = false) {
        this.loading = true;

        if(this.biDataItems.filter(item => item.type === this.type && item.data != null).length > 0 && !force){
            this.dataSource = this.biDataItems.filter(item => item.type === this.type)[0].data!;
            this.loading = false;

            return;
        }

        try {
            let type = this.type;
            let unit = '1억 KRW';
            let currentYear = uGetDateFormat('yyyy');
            let currentMonth = Number(uGetDateFormat('MM'));
            // 1~2월일 경우 year는 전년도 (예산 반기 기준)
            let year = this.selMonth < 3 ? String(Number(currentYear) - 1) : currentYear;
            let yearMonth = force ? (this.selMonth > currentMonth ? String(Number(currentYear) - 1) : currentYear) + uGetZero(this.selMonth) : uGetDateFormat('yyyyMM');
            let response = await this.getData(type, unit, year, yearMonth);
            // console.log('powerbi getDataSet', response);
            if(response.error != null){
                throw new Error(response.error.message); 
            }
            else if(response.results?.length == 0 || (response.results != null && response.results[0].tables?.length == 0)){
                throw new Error('no data');
            }
            else{
                this.dataSource = response.results[0].tables[0].rows;
                this.biDataItems = this.biDataItems.map(item => {
                    if(item.type === this.type){
                        return {
                            ...item,
                            data: this.dataSource
                        } as PowerBiDataItem;
                    }
                    else {
                        return item;
                    }
                });
                this.loading = false;
            }
        } catch (error) {
            console.error(error);
            this.loading = false;
        }
    }

    changeTab(value: PowerBiType) {
        console.debug('changeTab', value);
        this.type = value;
    }

    @Watch('type')
    onTypeChanged(newType: PowerBiType, oldType: PowerBiType) {
        if(newType != oldType){
            this.getDataSet(true);
        }
    }

    @Watch('selMonth')
    onSelMonthChanged(newMonth: string, oldMonth: string) {
        console.log('onSelMonthChanged', newMonth, oldMonth);

        if(newMonth != oldMonth){
            this.getDataSet(true);
        }
    }

    created() {
        this.init();
    }

    mounted() {

    }
}